import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  BookOpen,
  Briefcase,
  CheckCircle,
  Heart,
  Mail,
  MapPin,
  Phone,
  Shield,
  Star,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';

import { Footer } from '@components/Footer';
import { Header } from '@components/Header';
import { Logo } from '@components/Logo';
import { SEOHead } from '@components/SEOHead';

import { Button } from '@ui/Button';
import { Hero } from '@ui/Hero';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

export const AboutUsPage = () => {
  // Company values and mission
  const companyValues = [
    {
      icon: Shield,
      title: 'Trust & transparency',
      description:
        'We believe in complete transparency in all our dealings and recommendations, ensuring you always know where your money is going.',
    },
    {
      icon: Target,
      title: 'Goal-oriented approach',
      description:
        'Every investment recommendation is aligned with your specific financial goals and risk tolerance.',
    },
    {
      icon: Heart,
      title: 'Client-first philosophy',
      description:
        'Your financial success is our success. We prioritize your interests above everything else.',
    },
    {
      icon: TrendingUp,
      title: 'Long-term wealth creation',
      description:
        'We focus on sustainable, long-term wealth creation strategies rather than short-term market speculation.',
    },
  ];

  const qualifications = [
    {
      icon: Award,
      title: 'AMFI certified',
      description: `Certified Mutual Fund Distributor with ARN: ${CONTACT_INFO.amfi.arn}`,
    },
    {
      icon: BookOpen,
      title: 'Financial planning expertise',
      description:
        'Specialized in goal-based financial planning and investment advisory',
    },
    {
      icon: Users,
      title: 'Client-centric approach',
      description:
        'Helping individuals and families achieve their financial dreams',
    },
    {
      icon: Briefcase,
      title: 'Professional experience',
      description:
        'Years of experience in financial planning and investment advisory',
    },
  ];

  // Services offered
  const services = [
    {
      title: 'Mutual fund investment',
      description:
        'Expert guidance on selecting the right mutual funds based on your goals and risk profile',
      features: [
        'SIP planning',
        'Lumpsum investment',
        'Portfolio review',
        'Fund selection',
      ],
    },
    {
      title: 'Goal-based planning',
      description:
        'Comprehensive financial planning for your life goals like home, education, retirement',
      features: [
        'Retirement planning',
        'Child education',
        'Home purchase',
        'Emergency fund',
      ],
    },
    {
      title: 'Tax planning',
      description:
        'Optimize your tax liability through smart investment choices under various sections',
      features: [
        '80C investments',
        'ELSS funds',
        'Tax-saving strategies',
        'New vs old regime',
      ],
    },
    {
      title: 'Portfolio management',
      description:
        'Regular monitoring and rebalancing of your investment portfolio for optimal returns',
      features: [
        'Portfolio review',
        'Rebalancing',
        'Performance tracking',
        'Risk assessment',
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`About | ${CONTACT_INFO.company.name}`}
        description={`Learn about ${CONTACT_INFO.company.name} and our team, your trusted AMFI certified mutual fund distributor helping you achieve your financial goals.`}
        keywords={`about ${CONTACT_INFO.company.name}, ${CONTACT_INFO.contact_person.full_name}, AMFI certified, mutual fund distributor, financial advisor, investment planning`}
        canonicalUrl={`${CONTACT_INFO.social.website}/about`}
      />

      <div className="min-h-screen" style={{ backgroundColor: COLORS.pearl }}>
        <Header />

        <Hero
          title={
            <span className="flex items-center justify-center gap-2">
              About <Logo alt={CONTACT_INFO.company.name} fill="white" />
            </span>
          }
          description="Your trusted partner in building wealth and achieving financial freedom through smart investment choices"
          variant="gradient"
        >
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.gold }}
              >
                AMFI
              </div>
              <div className="text-white/70">certified MF distributor</div>
            </div>
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.sage }}
              >
                Trusted
              </div>
              <div className="text-white/70">investment partner</div>
            </div>
            <div className="text-center">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS.accent }}
              >
                Goal-Based
              </div>
              <div className="text-white/70">financial planning</div>
            </div>
          </div>
        </Hero>

        {/* Company Mission & Vision */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-6">
              <h2
                className="text-4xl font-bold mb-6"
                style={{ color: COLORS.primary }}
              >
                Our mission & vision
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto">
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                    style={{ backgroundColor: `${COLORS.accent}15` }}
                  >
                    <Target size={32} style={{ color: COLORS.accent }} />
                  </div>
                  <h3
                    className="text-2xl font-bold mb-4"
                    style={{ color: COLORS.primary }}
                  >
                    Our mission
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    To provide personalized, transparent, and goal-oriented
                    investment guidance that helps our clients make informed
                    financial decisions and achieve their life goals through
                    disciplined investing.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto"
                    style={{ backgroundColor: `${COLORS.sage}15` }}
                  >
                    <Star size={32} style={{ color: COLORS.sage }} />
                  </div>
                  <h3
                    className="text-2xl font-bold mb-4"
                    style={{ color: COLORS.primary }}
                  >
                    Our vision
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    To be a trusted financial partner for our clients, helping
                    them build lasting wealth through personalized investment
                    strategies, continuous guidance, and a deep understanding of
                    their unique financial aspirations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* About Team */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.mist }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-6">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Our team
              </h2>
            </div>

            <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-gray-100">
              <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 lg:gap-12">
                {/* Profile Picture */}
                <div className="flex-shrink-0">
                  <motion.div
                    className="w-48 h-48 sm:w-64 sm:h-64 rounded-2xl overflow-hidden shadow-lg border-2 border-white"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    whileHover={{ scale: 1.1 }}
                  >
                    <img
                      src="/vishal-2.jpg"
                      alt={`${CONTACT_INFO.contact_person.full_name} - ${CONTACT_INFO.contact_person.designation}`}
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                </div>

                {/* Content */}
                <div className="flex-1 text-center lg:text-left">
                  <div className="mb-6">
                    <h3
                      className="text-2xl sm:text-3xl font-bold mb-2"
                      style={{ color: COLORS.primary }}
                    >
                      {CONTACT_INFO.contact_person.full_name}
                    </h3>
                    <p
                      className="text-lg sm:text-xl mb-1"
                      style={{ color: COLORS.accent }}
                    >
                      {CONTACT_INFO.contact_person.designation} & founder of{' '}
                      {CONTACT_INFO.company.name}
                    </p>
                    <p className="text-sm text-gray-500 font-medium">
                      ARN: {CONTACT_INFO.amfi.arn}
                    </p>
                  </div>

                  <p className="text-gray-600 leading-relaxed mb-4">
                    {CONTACT_INFO.contact_person.short_name} is a passionate
                    advocate for financial literacy and wealth creation through
                    systematic investing. With his AMFI certification and deep
                    understanding of mutual funds, he helps individuals and
                    families navigate their investment journey with confidence
                    and clarity.
                  </p>

                  <p className="text-gray-600 leading-relaxed mb-6">
                    His approach is rooted in understanding each client's unique
                    financial situation, risk tolerance, and life goals. Whether
                    you're planning for your child's education, buying your
                    dream home, or securing your retirement,{' '}
                    {CONTACT_INFO.contact_person.short_name} provides
                    personalized guidance every step of the way.
                  </p>

                  <Button
                    href={`https://wa.me/${CONTACT_INFO.phone.whatsapp}?text=Hi ${CONTACT_INFO.contact_person.short_name}, I would like to discuss my investment goals with you.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="accent"
                    icon={ArrowRight}
                    iconSize={16}
                  >
                    Connect with {CONTACT_INFO.contact_person.short_name}
                  </Button>
                </div>
              </div>

              {/* Qualifications Grid */}
              <div className="grid grid-cols-1 gap-4 mt-8 pt-8">
                {qualifications.map((qual, index) => {
                  const IconComponent = qual.icon;
                  return (
                    <motion.div
                      key={qual.title}
                      className={`flex items-center p-4 rounded-xl bg-gray-50`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                    >
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
                        style={{ backgroundColor: `${COLORS.sage}15` }}
                      >
                        <IconComponent
                          size={24}
                          style={{ color: COLORS.sage }}
                        />
                      </div>
                      <div>
                        <h4
                          className="text-base font-bold mb-1"
                          style={{ color: COLORS.primary }}
                        >
                          {qual.title}
                        </h4>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {qual.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Company Values */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Our core values
              </h2>
              <p className="text-xl" style={{ color: COLORS.smoke }}>
                The principles that guide everything we do
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {companyValues.map((value, index) => {
                const IconComponent = value.icon;
                return (
                  <motion.div
                    key={value.title}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
                  >
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto"
                      style={{ backgroundColor: `${COLORS.gold}15` }}
                    >
                      <IconComponent size={32} style={{ color: COLORS.gold }} />
                    </div>
                    <h3
                      className="text-xl font-bold mb-3"
                      style={{ color: COLORS.primary }}
                    >
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {value.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* Services Offered */}
        <motion.section
          className="py-20"
          style={{
            background: `linear-gradient(135deg, ${COLORS.secondary} 0%, ${COLORS.primary} 100%)`,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-4xl font-bold mb-4"
                style={{ color: COLORS.pearl }}
              >
                Our services
              </h2>
              <p className="text-xl text-white/80">
                Comprehensive financial solutions for your investment journey
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:bg-white/15 transition-all duration-300"
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
                >
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-center text-sm text-white/80"
                      >
                        <CheckCircle
                          size={16}
                          className="mr-3 text-green-400"
                        />
                        {feature}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Contact Information */}
        <motion.section
          className="py-20"
          style={{ backgroundColor: COLORS.pearl }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2
              className="text-4xl font-bold mb-6"
              style={{ color: COLORS.primary }}
            >
              Let's start your investment journey
            </h2>
            <p className="text-xl mb-8" style={{ color: COLORS.smoke }}>
              Ready to take control of your financial future? Connect with{' '}
              {CONTACT_INFO.contact_person.short_name} today for personalized
              investment guidance.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <Phone
                  size={24}
                  style={{ color: COLORS.accent }}
                  className="mx-auto mb-3"
                />
                <h4
                  className="font-bold mb-2"
                  style={{ color: COLORS.primary }}
                >
                  Call us
                </h4>
                <a
                  href={`tel:${CONTACT_INFO.phone.primary}`}
                  className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
                >
                  {CONTACT_INFO.phone.display}
                </a>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <Mail
                  size={24}
                  style={{ color: COLORS.sage }}
                  className="mx-auto mb-3"
                />
                <h4
                  className="font-bold mb-2"
                  style={{ color: COLORS.primary }}
                >
                  Email us
                </h4>
                <a
                  href={`mailto:${CONTACT_INFO.email.general}?subject=Investment Inquiry`}
                  className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer"
                >
                  {CONTACT_INFO.email.general}
                </a>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <MapPin
                  size={24}
                  style={{ color: COLORS.gold }}
                  className="mx-auto mb-3"
                />
                <h4
                  className="font-bold mb-2"
                  style={{ color: COLORS.primary }}
                >
                  Meet us
                </h4>
                <a
                  href={CONTACT_INFO.address.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 text-sm hover:text-blue-600 transition-colors duration-200 cursor-pointer"
                >
                  {CONTACT_INFO.address.full}
                </a>
              </div>
            </div>

            <Button
              href={`https://wa.me/${CONTACT_INFO.phone.whatsapp}?text=Hi ${CONTACT_INFO.contact_person.short_name}, I'm interested in learning more about investment opportunities and would like to schedule a consultation.`}
              target="_blank"
              rel="noopener noreferrer"
              variant="sage"
              size="lg"
              icon={ArrowRight}
              iconSize={20}
            >
              Schedule a consultation
            </Button>
          </div>
        </motion.section>

        <Footer />
      </div>
    </>
  );
};
